import { fetchBlobCached } from "../../hooks/useBlobUrl";
import { istBildBreite } from "./bildBreite";

export { NOTIZBILD_MIN_BREITE, begrenzeBildBreite, istBildBreite } from "./bildBreite";

/**
 * Wie ein Bild im Notiztext AUSSIEHT (PO 14.09.2026: „bilder in die notiz
 * laden, links wie in Notion") — und wie es wieder verschwindet, bevor
 * gespeichert wird. Dasselbe Muster wie `bezugDarstellung.ts` daneben.
 *
 * ══ EIN SPEICHERWEG, KEIN BASE64 ═══════════════════════════════════════════
 * Ein Bild ist ein Dokument im S3-Speicher (`POST /documents/upload` mit
 * `category: 'Notizbild'`). In der Notiz steht nur der schlanke Marker:
 *
 *     <img data-bild-id="123" alt="Screenshot.png">
 *
 * Beim Anzeigen lädt {@link hydratisiereBilder} das Bild AUTHENTIFIZIERT per
 * `fetch` (der Wrapper in `utils/api.ts` hängt den Bearer an) und hängt es als
 * `blob:`-URL an den Marker. Vor dem Speichern nimmt {@link entwaesserBilder}
 * die URL wieder ab.
 *
 * Warum der Umweg:
 *  · Base64 inline hätte jeden Autosave (alle 1,2 s Ruhe) und jeden
 *    Live-Frame an alle Beteiligten mit Megabytes gefüllt und die
 *    Regex-Suche des Archivs zäh gemacht.
 *  · Ein nacktes `<img src="/api/documents/…">` trüge keinen Bearer — das
 *    Frontend ist Bearer-only (PR #582), und einen Token ins gespeicherte
 *    HTML zu schreiben wäre ein Leck.
 *  · Eine `blob:`-URL lebt nur in dieser Sitzung; gespeichert wäre sie beim
 *    nächsten Öffnen tot. Deshalb darf sie NIE in die Datenbank — und der
 *    Vergleich „hat sich etwas geändert" in `NotizBlock` läuft auf dem
 *    entwässerten Stand, sonst speicherte jede Hydration.
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Bewusst OHNE React: im contentEditable ist das DOM die Quelle der Wahrheit,
 * und diese Umformung ist der Teil, der schiefgehen kann. Als freie
 * Funktionen ist sie im jsdom prüfbar.
 */

/** `Document.category` eines Notizbilds — Spiegel von `apps/api/src/notizbild.util.ts`. */
export const KATEGORIE_NOTIZBILD = "Notizbild";

/** Nur Rasterbilder — dieselbe Liste wie im Mail-Editor und im Backend. Kein SVG. */
export const NOTIZBILD_MIME = ["image/png", "image/jpeg", "image/gif", "image/webp"] as const;

/** Obergrenze je Bild; das Backend prüft dieselbe Zahl. */
export const NOTIZBILD_MAX_BYTES = 8 * 1024 * 1024;

/**
 * Darf diese Datei ein Notizbild werden? Liefert den Text für den Nutzer,
 * wenn nicht — `null` heißt: ja.
 */
export function pruefeNotizbild(file: File): string | null {
    if (!(NOTIZBILD_MIME as readonly string[]).includes(file.type)) {
        return `„${file.name}": nur PNG, JPG, GIF oder WEBP lassen sich als Bild einfügen.`;
    }
    if (file.size > NOTIZBILD_MAX_BYTES) {
        return `„${file.name}" ist zu groß (höchstens ${Math.round(NOTIZBILD_MAX_BYTES / (1024 * 1024))} MB je Bild).`;
    }
    return null;
}

/**
 * Der Bildtyp eines Notizbilds aus seinem Namen (`alt` des Markers). Der Marker
 * trägt keinen MIME-Typ; ohne erkennbare Endung gilt PNG — Notizbilder sind
 * ohnehin nur PNG, JPG, GIF oder WEBP ({@link NOTIZBILD_MIME}). Der Viewer
 * braucht den Typ, um das Bild als BILD zu zeigen.
 */
export function bildMimeAusName(name: string): (typeof NOTIZBILD_MIME)[number] {
    const endung = /\.([a-z0-9]+)$/i.exec(name.trim())?.[1]?.toLowerCase();
    if (endung === "jpg" || endung === "jpeg") return "image/jpeg";
    if (endung === "gif") return "image/gif";
    if (endung === "webp") return "image/webp";
    return "image/png";
}

/** Der schlanke Marker eines eigenen Notizbilds — genau das, was gespeichert wird. */
export function bildMarker(id: number, alt: string, breite?: number | null): HTMLImageElement {
    const img = document.createElement("img");
    img.setAttribute("data-bild-id", String(id));
    img.setAttribute("alt", alt);
    if (breite != null && istBildBreite(String(breite))) img.setAttribute("width", String(breite));
    return img;
}


/**
 * Ein externes Bild (Notion-Stil „Embed link"): bleibt Live-Verweis, wird
 * nicht hochgeladen. Nur `https://` — `http:` wäre gemischter Inhalt, alles
 * andere kein Bild. `referrerpolicy=no-referrer`, damit die Notiz dem
 * fremden Host nicht verrät, woher sie kommt; `loading=lazy`, weil ein
 * Vorgang Dutzende Blöcke haben kann.
 */
export function externesBild(url: string): HTMLImageElement | null {
    const u = url.trim();
    if (!/^https:\/\/\S+$/i.test(u)) return null;
    const img = document.createElement("img");
    img.setAttribute("src", u);
    img.setAttribute("referrerpolicy", "no-referrer");
    img.setAttribute("loading", "lazy");
    img.setAttribute("alt", "");
    return img;
}

/**
 * Was während des Hochladens an der Stelle steht, an der das Bild hinkommt.
 * `entwaesserBilder` entfernt ihn — ein Autosave mitten im Upload darf den
 * Hinweistext nicht als Notiz speichern.
 */
export function bildPlatzhalter(name: string): HTMLElement {
    const el = document.createElement("span");
    el.className = "nb-bild-platzhalter";
    el.setAttribute("contenteditable", "false");
    el.textContent = `Bild „${name}" wird hochgeladen …`;
    return el;
}

export type BildLader = (id: number) => Promise<Blob>;

/** Der Normalfall: authentifiziert über den geteilten Blob-Cache laden. */
export const ladeNotizbild: BildLader = (id) => fetchBlobCached(`documents/${id}/download`);

/**
 * Jeden Marker im Feld zu einem sichtbaren Bild machen.
 *
 * Geladen wird nur, was noch keine Quelle hat — ein zweiter Aufruf (nach
 * dem Einfügen eines weiteren Bilds) lädt nicht alles neu. Jede erzeugte
 * `blob:`-URL geht an `merke`, damit der Aufrufer sie beim Unmount wieder
 * freigibt: der Blob liegt im geteilten Cache, die Object-URL gehört diesem
 * Feld.
 *
 * Schlägt das Laden fehl (gelöschtes Dokument, kein Recht), trägt der
 * Marker die Klasse `nb-bild-fehler` und bleibt als Kasten mit seinem
 * `alt` stehen — der nächste Aufruf versucht es erneut.
 */
export async function hydratisiereBilder(
    wurzel: HTMLElement,
    opts: { laden?: BildLader; merke?: (url: string) => void } = {},
): Promise<void> {
    const laden = opts.laden ?? ladeNotizbild;
    const offen = Array.from(wurzel.querySelectorAll<HTMLImageElement>("img[data-bild-id]")).filter(
        (img) => !img.getAttribute("src") && !img.classList.contains("nb-bild-laedt"),
    );
    await Promise.all(
        offen.map(async (img) => {
            const id = Number(img.getAttribute("data-bild-id"));
            if (!Number.isFinite(id) || id <= 0) return;
            img.classList.add("nb-bild", "nb-bild-laedt");
            img.classList.remove("nb-bild-fehler");
            try {
                const blob = await laden(id);
                // Das Feld kann inzwischen leergeräumt oder demontiert sein —
                // dann gibt es nichts mehr zu zeigen und nichts zu merken.
                if (!img.isConnected) return;
                const url = URL.createObjectURL(blob);
                opts.merke?.(url);
                img.setAttribute("src", url);
            } catch {
                img.classList.add("nb-bild-fehler");
                if (!img.getAttribute("alt")) img.setAttribute("alt", "Bild konnte nicht geladen werden");
            } finally {
                img.classList.remove("nb-bild-laedt");
            }
        }),
    );
}

/**
 * Die Umkehrung: aus jedem angezeigten Notizbild wieder der schlanke Marker,
 * und der Upload-Platzhalter verschwindet ganz.
 *
 * Läuft auf einer LOSGELÖSTEN Kopie des Feldes — beim Speichern schaut der
 * Nutzer noch auf das Bild, und es unter ihm auszutauschen risse den Cursor
 * weg. Externe Bilder (`src=https://…`) bleiben, wie sie sind: sie SIND ihr
 * Verweis.
 */
export function entwaesserBilder(html: string): string {
    if (!/<(?:img|span)\b/i.test(html ?? "")) return html ?? "";
    const topf = document.createElement("div");
    topf.innerHTML = html ?? "";

    for (const img of Array.from(topf.querySelectorAll<HTMLImageElement>("img[data-bild-id]"))) {
        const id = Number(img.getAttribute("data-bild-id"));
        if (!Number.isFinite(id) || id <= 0) {
            img.remove();
            continue;
        }
        const breite = img.getAttribute("width");
        img.replaceWith(bildMarker(id, img.getAttribute("alt") ?? "", istBildBreite(breite) ? Number(breite) : null));
    }
    for (const el of Array.from(topf.querySelectorAll(".nb-bild-platzhalter"))) el.remove();

    return topf.innerHTML;
}

/** Alle gemerkten Object-URLs freigeben — beim Unmount des Feldes. */
export function gibBilderFrei(urls: Iterable<string>): void {
    for (const url of urls) {
        try {
            URL.revokeObjectURL(url);
        } catch {
            // Eine schon freigegebene URL ist kein Fehler.
        }
    }
}
